import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import DetailedTable from '@repo/ui/detailed-table'
import { EuroIcon, InformationIcon } from '@repo/ui/icons'
import InformationBox from '@repo/ui/information-box'
import InformationToggle from '@repo/ui/information-toggle'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getStaffPayScales } from '../../api/staff-pay-scales/get-staff-pay-scales'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import StaffPersonalSpaceAatPayScale from '../../components/pages/staff-personal-space/staff-pay-scale/staff-personal-space-aat-pay-scale'
import PayScaleInformation from '../../components/pages/staff-personal-space/staff-pay-scale/staff-personal-space-pay-scale-information'
import useAuth from '../../lib/hooks/use-auth'
import useTranslate from '../../lib/hooks/use-translate'
import {
  StaffPositionEnum,
  staffPositionEnumMapping
} from '../../lib/types/enums/staff/staff-position.enum'
import { getQualificationsCurrencySymbol } from '../../lib/types/enums/staff/staff-qualifications.enum'
import { PayScaleTypeEnum } from '../../lib/types/enums/staff-pay-scale/pay-scale-type.enum'
import { CountryKey } from '../../lib/types/models/country/country-list'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'
import {
  dailySalarySliceTypeMapping,
  mapStaffPayScaleToTableData,
  StaffPayScaleDetail
} from './utils'

const StaffPayScaleDefault = () => {
  const { t } = useTranslate()
  const { user } = useAuth()

  const { data: staffPayScales, isPending } = useQuery({
    queryKey: ['staff-pay-scales'],
    queryFn: () => getStaffPayScales({ filter: { type: PayScaleTypeEnum.DAILY_SALARY } })
  })

  const position = `${t(staffPositionEnumMapping[user?.staff?.position ?? StaffPositionEnum.TEACHER]).toLowerCase()}s`

  if (isPending || !staffPayScales) return <LoadingScreen />

  return (
    <div className="flex flex-col gap-5 h-screen">
      <UserNavBar
        title={t('staff.personal.space.pay.scale.title', { position })}
        icon={EuroIcon}
      />
      <Box className="px-5">
        <Box className="relative bg-white-active rounded-xl px-3 py-3 hidden mobile:block">
          <InformationToggle>
            <PayScaleInformation position={position} />
          </InformationToggle>

          <div className="flex items-start gap-1">
            <InformationIcon
              color={tailwindColors.secondaryRed.DEFAULT}
              className="h-6 w-6 shrink-0"
            />
            <Typography variant="caption" color={tailwindColors.black.DEFAULT} className="pr-14">
              {t('staff.personal.space.pay.scale.terms.note')}
            </Typography>
          </div>
        </Box>

        <InformationBox>
          <PayScaleInformation position={position} />
        </InformationBox>
        <div className="h-6" />
        {staffPayScales.data
          .filter((data) => mapStaffPayScaleToTableData(data).length > 0)
          .map((data) => {
            const currency = getQualificationsCurrencySymbol(data)
            return (
              <>
                <Typography variant="h6" color={tailwindColors.black.DEFAULT} className="pl-2">
                  {data.name}
                </Typography>
                {data.description && (
                  <Typography
                    variant="label-medium"
                    color={tailwindColors.black.DEFAULT}
                    className="pl-2"
                  >
                    {data.description}
                  </Typography>
                )}
                <div className="h-4" />
                <DetailedTable<StaffPayScaleDetail>
                  groupBy="formulaId"
                  data={mapStaffPayScaleToTableData(data)}
                  columns={[
                    {
                      key: 'formulaId',
                      header: data.countries.map((country) => country.name).join(', '),
                      render: (value) => (
                        <>
                          <Typography variant="label-semibold">
                            {getTranslatedName(value.formula)}
                          </Typography>
                          <Typography variant="small-medium">
                            ({value.formula.description})
                          </Typography>
                        </>
                      )
                    },
                    {
                      key: 'dailySalarySliceType',
                      header: '',
                      render: (value) => (
                        <Typography variant="label-semibold">
                          {dailySalarySliceTypeMapping[value.dailySalarySliceType]}
                        </Typography>
                      ),
                      highlight: true
                    },
                    {
                      header: t('staff.personal.space.pay.scale.table.per.day', { currency }),
                      key: 'dailySalarySlice',
                      render: (value) => {
                        return (
                          <div className="flex justify-center items-center w-full">
                            <Badge
                              text={value.dailySalarySlice.toString()}
                              variant="primary"
                              size="small"
                            />
                          </div>
                        )
                      }
                    },
                    {
                      header: t('staff.personal.space.pay.scale.table.per.8.days', { currency }),
                      key: 'dailySalarySlicex8',
                      render: (value) => {
                        return (
                          <div className="flex justify-center items-center w-full">
                            <Badge
                              text={value.dailySalarySlicex8.toString()}
                              variant="grey"
                              size="small"
                            />
                          </div>
                        )
                      }
                    },
                    {
                      header: t('staff.personal.space.pay.scale.table.per.13.days', { currency }),
                      key: 'dailySalarySlicex13',
                      render: (value) => {
                        return (
                          <div className="flex justify-center items-center w-full">
                            <Badge
                              text={value.dailySalarySlicex13.toString()}
                              variant="grey"
                              size="small"
                            />
                          </div>
                        )
                      }
                    },
                    {
                      header: data.countries.some(
                        (country) => country.code === CountryKey.MLT.toString()
                      )
                        ? t('staff.personal.space.pay.scale.table.per.14.days.malta', { currency })
                        : t('staff.personal.space.pay.scale.table.per.14.days', { currency }),
                      key: 'dailySalarySlicex14',
                      render: (value) => {
                        return (
                          <div className="flex justify-center items-center w-full">
                            <Badge
                              text={value.dailySalarySlicex14.toString()}
                              variant="grey"
                              size="small"
                            />
                          </div>
                        )
                      }
                    }
                  ]}
                />
                <div className="h-6" />
              </>
            )
          })}
      </Box>
    </div>
  )
}

const StaffPersonalSpacePayScaleTable = () => {
  const { t } = useTranslate()
  const { user } = useAuth()

  if (user?.staff?.position === StaffPositionEnum.AAT)
    return (
      <div className="flex flex-col gap-5 h-screen">
        <UserNavBar title={t('staff.personal.space.pay.scale.aat.title')} icon={EuroIcon} />
        <StaffPersonalSpaceAatPayScale />
      </div>
    )

  return <StaffPayScaleDefault />
}

export default StaffPersonalSpacePayScaleTable
